import { omit } from 'lodash';

import { CustomPropertyValues, Entity, Producer } from '@evidentid/tprm-portal-lib/models/dashboard';
import { CustomPropertyCollateral } from '@evidentid/tprm-portal-lib/models/dashboard/CustomProperty.model';
import { EntityAddress } from '@evidentid/tprm-portal-lib/models/dashboard/EntityAddress.model';

import { ProducerRow, toProducers, withDefaultFallback } from '../producer-forms/producerRows';

function concatCollateralsCustomProperties(
    customPropertyValues?: CustomPropertyValues,
    collaterals?: CustomPropertyCollateral[],
    collateralKey?: string,
): CustomPropertyValues | null {
    if (!customPropertyValues && !collaterals) {
        return null;
    }
    return {
        ...customPropertyValues,
        ...(collaterals && collateralKey && { [collateralKey]: collaterals }),
    };
}

/** The editable shape the entity details form binds to. */
export interface EntityDetailsValue {
    displayName: string;
    body: {
        profile: {
            legalName?: string | null;
            doingBusinessAs?: string[] | null;
            entityUuid?: string;
            contactEmail: string;
            contactName?: string;
            contactPhoneNumber?: string | null;
            address?: EntityAddress | null;
        };
        insuredFields?: CustomPropertyValues;
    };
}

export interface EntityFormProfile {
    doingBusinessAs?: string[] | null;
    address?: unknown;
    [key: string]: unknown;
}

export interface BuildEntityToBeSavedInput {
    entity: Entity;
    profile: EntityFormProfile;
    insuredFields?: CustomPropertyValues;
    pendingCollaterals?: CustomPropertyCollateral[];
    collateralCustomPropertyKey: string;
    /** Edited producer rows, or undefined while the producers card has not seeded yet. */
    pendingProducers?: ProducerRow[];
    producerFlowEnabled: boolean;
}

/**
 * Resolves `doingBusinessAs`.
 *
 * The form's ignoreOptionalEmpty strips the field when it becomes an empty array, which is
 * ambiguous — so the two readings are separated: names the user cleared save as an empty array
 * (a deletion), while a form that merely initialized as `[]` keeps the entity's own value.
 */
function resolveDoingBusinessAs(entity: Entity, profile: EntityFormProfile): string[] | null | undefined {
    const raw = profile.doingBusinessAs;
    const filtered = Array.isArray(raw) ? raw.filter((name: string) => name?.trim() !== '') : raw;
    if (filtered !== undefined) {
        return filtered;
    }
    return entity.doingBusinessAs?.length ? [] : entity.doingBusinessAs;
}

/**
 * Resolves the `address` key.
 *
 * ignoreOptionalEmpty may drop an optional null/empty field from the profile, so spreading the
 * entity first and the profile second would fall back to the old address and make a deletion look
 * like no change. Three cases: the profile carries an address (use it, `null` included); the
 * profile dropped it but the entity had one (the user deleted it → `null`); neither has one (omit
 * the key entirely).
 */
function resolveAddressOverride(entity: Entity, profile: EntityFormProfile): { address?: unknown } {
    if ('address' in profile) {
        return { address: profile.address };
    }
    return (entity as { address?: unknown }).address == null ? {} : { address: null };
}

const CLEARABLE_STRING_KEYS = [ 'legalName', 'contactName', 'contactPhoneNumber' ] as const;
type ClearableStringKey = typeof CLEARABLE_STRING_KEYS[number];

/**
 * Resolves an optional standard-property field that ignoreOptionalEmpty may have dropped from the
 * profile, the same ambiguity `resolveAddressOverride` untangles for `address`: spreading the
 * entity first and the profile second would fall back to the old value and make a deletion look
 * like no change. Three cases: the profile carries a value (use it); the profile dropped it but the
 * entity had one (the user cleared it → `null`); neither has one (omit the key entirely).
 *
 * A falsy check, not `== null`, matches `resolveDoingBusinessAs`'s own empty check — a stored
 * empty string reads as "never set" here too, so it isn't mistaken for a value the user cleared.
 */
function resolveClearableStringOverride(
    entity: Entity, profile: EntityFormProfile, key: ClearableStringKey,
): Partial<Record<ClearableStringKey, string | null>> {
    if (key in profile) {
        return { [key]: profile[key] };
    }
    return entity[key] ? { [key]: null } : {};
}

/**
 * Resolves the `producerList` key.
 *
 * The key is omitted until the producers card has seeded its rows, and is never introduced on an
 * entity that never carried one: an unsolicited `producerList: []` would read as an edit, and on a
 * relying party with the producer flow disabled, as a deletion of data the UI never showed.
 */
function resolveProducerOverride(
    entity: Entity, pendingProducers: ProducerRow[] | undefined, producerFlowEnabled: boolean,
): { producerList?: Producer[] } {
    const seeded = producerFlowEnabled && pendingProducers !== undefined;
    const relevant = entity.producerList !== undefined || (pendingProducers || []).length > 0;
    return seeded && relevant ? { producerList: toProducers(pendingProducers as ProducerRow[]) } : {};
}

/** Builds the entity payload a save sends, overlaying the form's edits onto the loaded entity. */
export function buildEntityToBeSaved(input: BuildEntityToBeSavedInput): Entity {
    const {
        entity, profile, insuredFields, pendingCollaterals,
        collateralCustomPropertyKey, pendingProducers, producerFlowEnabled,
    } = input;

    const mergedInsuredFields = concatCollateralsCustomProperties(
        insuredFields || {},
        pendingCollaterals,
        collateralCustomPropertyKey,
    );

    // `address` and the clearable string keys are dropped so the profile spread below cannot
    // reintroduce a stale value for a field the form cleared (see their resolvers). `entityUuid` is
    // display-only — it mirrors the entity's own `id` and is never sent back.
    const {
        address: _address, entityUuid: _entityUuid,
        legalName: _legalName, contactName: _contactName, contactPhoneNumber: _contactPhoneNumber,
        ...profileRest
    } = profile;

    return {
        ...entity,
        ...profileRest,
        ...resolveAddressOverride(entity, profile),
        ...CLEARABLE_STRING_KEYS.reduce(
            (acc, key) => ({ ...acc, ...resolveClearableStringOverride(entity, profile, key) }),
            {} as Partial<Record<ClearableStringKey, string | null>>,
        ),
        doingBusinessAs: resolveDoingBusinessAs(entity, profile),
        ...(mergedInsuredFields !== null && { insuredFields: mergedInsuredFields }),
        ...resolveProducerOverride(entity, pendingProducers, producerFlowEnabled),
    } as Entity;
}

/**
 * Normalizes the fields whose representation differs between a loaded entity and a freshly built
 * payload without any real edit having happened, so a dirty check compares like with like:
 * `doingBusinessAs` is initialized as `[]` by the form even when the entity has no names, and
 * producer values are trimmed on the way out while coverage-type order carries no meaning.
 */
export function normalizeEntityForDirtyCheck(entity: Entity): Entity {
    return {
        ...entity,
        doingBusinessAs: (entity.doingBusinessAs || []).filter((name: string) => name?.trim() !== ''),
        // Same fallback as the editable rows, so a default seeded on open is not an unsaved change.
        producerList: withDefaultFallback((entity.producerList || []).map((producer) => ({
            ...producer,
            isDefault: Boolean(producer.isDefault),
        }))).map((producer) => ({
            ...producer,
            producerFirm: (producer.producerFirm || '').trim(),
            contactName: (producer.contactName || '').trim(),
            email: (producer.email || '').trim(),
            phone: (producer.phone || '').trim(),
            coverageTypes: [ ...(producer.coverageTypes || []) ].sort(),
        })),
    };
}

/** Seeds the form's editable value from a loaded entity. Collaterals are excluded because they
 * are edited through their own pending state, not as a custom-property value. */
export function buildEntityFormValue(entity: Entity, collateralCustomPropertyKey: string): EntityDetailsValue {
    return {
        displayName: entity.displayName,
        body: {
            profile: {
                legalName: entity.legalName,
                doingBusinessAs: entity.doingBusinessAs?.length ? entity.doingBusinessAs : [],
                entityUuid: entity.id,
                contactEmail: entity.contactEmail,
                contactName: entity.contactName,
                contactPhoneNumber: entity.contactPhoneNumber,
                address: entity.address,
            },
            insuredFields: omit({ ...entity.insuredFields }, collateralCustomPropertyKey),
        },
    };
}
